import React, { useCallback, ReactNode } from "react";
import { useCookies } from "react-cookie";
import { AuthContext, type AuthContextType } from "./AuthContext";

const AUTH_TOKEN_KEY = "userAuthToken";

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Provider component for authentication context
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Child components
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [cookies, setCookie] = useCookies();

  const AuthToken = cookies[AUTH_TOKEN_KEY] || "";

  const setToken = useCallback(
    (token: string) => {
      try {
        setCookie(AUTH_TOKEN_KEY, token, {
          path: "/",
        });
        // Slack login message is now sent from fillUserWithMe after user data is cached
      } catch {
        // silently catch cookie setting errors
      }
    },
    [setCookie],
  );

  const unsetToken = useCallback(() => {
    try {
      setCookie(AUTH_TOKEN_KEY, "", {
        maxAge: -1,
        path: "/",
      });
    } catch {
      // silently catch cookie clearing errors
    }
  }, [setCookie]);

  const value: AuthContextType = {
    AuthToken,
    setToken,
    unsetToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
